import {format} from 'date-fns';

import {ConfigConstant} from '../constants/config';
import StorageUtils from '../utils/storage';

const logOutUser = () => {
  if (!ConfigConstant.LOGOUT_URL?.includes(window.location.host)) {
    StorageUtils.removeAllStorage();
  }
  window.location.href = ConfigConstant.LOGOUT_URL;
};

const goToHome = () => {
  window.location.href = ConfigConstant.APP_HOME || '/';
};

const getDateFromText = (text) => {
  const date = new Date();
  const result = [null, null];
  switch (text) {
    case 'Today': {
      result[0] = new Date(date.setHours(0, 0, 0, 0)).toISOString();
      result[1] = new Date(date.setHours(23, 59, 59, 999)).toISOString();
      break;
    }
    case 'Yesterday': {
      date.setDate(date.getDate() - 1);
      result[0] = new Date(date.setHours(0, 0, 0, 0)).toISOString();
      result[1] = new Date(date.setHours(23, 59, 59, 999)).toISOString();
      break;
    }
    case 'This Week': {
      result[1] = new Date(date.setHours(23, 59, 59, 999)).toISOString();
      date.setDate(date.getDate() - date.getDay());
      result[0] = new Date(date.setHours(0, 0, 0, 0)).toISOString();
      break;
    }
    case 'Last 7 Days': {
      result[1] = new Date(date.setHours(23, 59, 59, 999)).toISOString();
      date.setDate(date.getDate() - 7);
      result[0] = new Date(date.setHours(0, 0, 0, 0)).toISOString();
      break;
    }
    case 'This Month': {
      result[1] = new Date(date.setHours(23, 59, 59, 999)).toISOString();
      date.setDate(date.getDate() - date.getDate());
      result[0] = new Date(date.setHours(0, 0, 0, 0)).toISOString();
      break;
    }
    case 'Last 30 Days': {
      result[1] = new Date(date.setHours(23, 59, 59, 999)).toISOString();
      date.setDate(date.getDate() - 30);
      result[0] = new Date(date.setHours(0, 0, 0, 0)).toISOString();
      break;
    }
    default: {
      break;
    }
  }
  return result;
};

const formatChannelName = (data) => {
  if (data?.channelCode && data?.channelGroupCode) {
    if (data.channelGroupCode === data.channelCode) {
      return `${data?.channelName}-direct`;
    }
    return `${data?.channelGroupName}-${data?.channelName}`;
  } else {
    return '';
  }
};

const formatChannelCode = (data) => {
  return `${data.channelGroupCode}-${data.channelCode}`;
};

const convertObjectToURLParams = (object) => {
  const result = [];
  const recurse = (cur, prop) => {
    if (Object(cur) !== cur) {
      result.push(encodeURIComponent(prop) + '=' + encodeURIComponent(cur));
    } else if (Array.isArray(cur)) {
      cur.forEach((item, i) => recurse(item, prop + '[' + i + ']'));
    } else {
      Object.keys(cur).forEach((key) => recurse(cur[key], prop ? prop + '.' + key : key));
    }
  };
  recurse(object, '');
  return result.join('&');
};

const setObjectValue = (obj, path, value) => {
  path.split('.').reduce((acc, key, index, arr) => {
    const arrayMatch = key.match(/(\w+)\[(\d+)\]/);
    if (arrayMatch) {
      // eslint-disable-next-line no-unused-vars
      const [_, prop, idx] = arrayMatch;
      acc[prop] = acc[prop] || [];
      acc[prop][idx] = acc[prop][idx] || (index === arr.length - 1 ? value : {});
      return acc[prop][idx];
    } else {
      acc[key] = acc[key] || (index === arr.length - 1 ? value : {});
      return acc[key];
    }
  }, obj);
};

const convertURLParamsToObject = (queryString) => {
  const params = new URLSearchParams(queryString);
  const result = {};
  params.forEach((value, key) => setObjectValue(result, key, value));
  return result;
};

const getAppliedFilterValues = (filterId, value, commonFilters) => {
  const filterValue = {
    value,
    displayValue: null,
  };
  const filterMappings = {
    channelCode: 'channelCode',
    partnerBrandCode: 'partnerBrandCode',
    partnerPhyStoreCode: 'partnerPhyStoreCode',
    operationType: 'operationType',
    toggleType: 'toggleType',
    status: 'status',
    groupBy: 'groupBy',
  };
  if (filterId === 'date') {
    const [dateFrom, dateTo] = value;
    filterValue.displayValue = `${format(new Date(dateFrom), 'dd MMM yyyy')} - ${format(new Date(dateTo), 'dd MMM yyyy')}`;
  } else if (filterMappings[filterId]) {
    if (Array.isArray(value) && value.length > 0) {
      filterValue.displayValue = [];
      value.forEach((ele) => {
        const filterKey = filterMappings[filterId];
        const foundItem = commonFilters[filterKey]?.find((item) => item.code === ele);
        if (foundItem) {
          filterValue.displayValue.push(foundItem.label);
        }
      });
    } else {
      const foundItem = commonFilters[filterId]?.find((item) => item.code === value);
      if (foundItem) {
        filterValue.displayValue = foundItem.label;
      }
    }
  } else {
    filterValue.displayValue = value;
  }
  return filterValue;
};

const getDefaultFilters = (urlParams, commonFilters) => {
  const defaultFilters = convertURLParamsToObject(urlParams) || {};
  Object.keys(defaultFilters).forEach((key) => {
    defaultFilters[key] = getAppliedFilterValues(key, defaultFilters[key], commonFilters);
  });
  return defaultFilters;
};

const getAppliedFilters = ({filters}) => {
  const dateQuery = {from: null, to: null};
  const filterQuery = {};
  filters?.forEach((filter) => {
    if (filter?.id === 'Date') {
      const [from, to] = filter?.value || [];
      dateQuery.from = from;
      dateQuery.to = to;
    } else if (filter.id === 'Channel') {
      if (Array.isArray(filter?.value) && filter?.value.length > 0) {
        filterQuery.channelCode = [];
        filterQuery.channelGroupCode = [];
        filter?.value?.forEach((channel) => {
          const [channelGroupCode, channelCode] = channel?.split('-') || [];
          filterQuery.channelCode.push(channelCode);
          filterQuery.channelGroupCode.push(channelGroupCode);
        });
      }
    } else if (filter?.isApplied) {
      filterQuery[filter?.key] = filter?.value;
    }
  });
  return {filterQuery, dateQuery};
};

export {
  goToHome,
  logOutUser,
  getDateFromText,
  formatChannelName,
  formatChannelCode,
  getAppliedFilters,
  getDefaultFilters,
  convertObjectToURLParams,
  convertURLParamsToObject,
};
